import { CollapsibleFilters } from '../../components/common/CollapsibleFilters';
import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Grid,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Select,
  Option,
  Stack,
  Typography,
} from "@mui/joy";
import api from "../../lib/api";
import type { QRCodeRecord } from "../../types/marketing";
import { CreateQRCodeModal } from "../../components/marketing/CreateQRCodeModal";
import { createQR, exportQR } from "../../components/marketing/qrRendering";
import toast from "react-hot-toast";

const QRThumbnail = ({ record }: { record: QRCodeRecord }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    element.replaceChildren();
    let active = true;
    try {
      const qr = createQR(record, 220);
      qr.getRawData("svg")
        .then(() => {
          if (active) {
            qr.append(element);
            setFailed(false);
          }
        })
        .catch(() => {
          if (active) setFailed(true);
        });
    } catch {
      setFailed(true);
    }
    return () => {
      active = false;
    };
  }, [record]);
  return (
    <Box>
      {failed && (
        <Typography color="warning">
          Preview unavailable. Edit the code to check its logo and destination.
        </Typography>
      )}
      <Box
        ref={ref}
        sx={{
          mx: "auto",
          maxWidth: 220,
          "& svg": { width: "100%", height: "auto" },
        }}
      />
    </Box>
  );
};
type Stats = {
  period_end: string;
  total_codes: number;
  total_scans: number;
  daily_scans: { date: string; scans: number }[];
  tracking_note: string;
};
export const QRCodes: React.FC = () => {
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<QRCodeRecord | null>(null);
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [deleteIds, setDeleteIds] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showTrends, setShowTrends] = useState(false);
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [search, kind, status]);
  const refresh = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["qr-codes"] }),
      client.invalidateQueries({ queryKey: ["qr-codes-stats"] }),
    ]);
  };
  const query = useQuery({
    queryKey: ["qr-codes", page, search, kind, status],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), search });
      if (kind !== "all") params.set("qr_type", kind);
      if (status !== "all") params.set("status", status);
      return (
        await api.get<{
          results: QRCodeRecord[];
          count: number;
          next: string | null;
        }>(`/api/marketing/qr-codes/?${params}`)
      ).data;
    },
  });
  const stats = useQuery({
    queryKey: ["qr-codes-stats"],
    queryFn: async () =>
      (await api.get<Stats>("/api/marketing/qr-codes/stats/")).data,
  });
  const operate = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError("");
    try {
      await action();
      await refresh();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "Action failed. Please retry.",
      );
    } finally {
      setBusy(false);
    }
  };
  const download = (record: QRCodeRecord, format: "png" | "pdf") =>
    operate(async () => {
      const fresh = (
        await api.get<QRCodeRecord>(`/api/marketing/qr-codes/${record.id}/`)
      ).data;
      await exportQR(fresh, format);
    });
  const days = Array.from({ length: 30 }, (_, index) => {
    const date = stats.data?.period_end
      ? new Date(`${stats.data.period_end}T12:00:00`)
      : new Date();
    date.setDate(date.getDate() - (29 - index));
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    return {
      date: key,
      scans:
        stats.data?.daily_scans.find((day) => day.date === key)?.scans || 0,
    };
  });
  const maxScans = Math.max(1, ...days.map((day) => day.scans));
  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{ mb: 2 }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
          <Button variant="outlined" onClick={refresh}>
            Refresh
          </Button>
          <Button variant="plain" onClick={() => setShowTrends(!showTrends)}>
            Scan trends
          </Button>
          {stats.data && (
            <Chip>
              {stats.data.total_codes} codes · {stats.data.total_scans} tracked
              scans
            </Chip>
          )}
        </Stack>
        <Button
          onClick={() => {
            setEditing(null);
            setOpen(true);
          }}
        >
          Create QR Code
        </Button>
      </Stack>
      {(query.isError || stats.isError || error) && (
        <Alert
          color="danger"
          sx={{ mb: 2 }}
          endDecorator={<Button onClick={refresh}>Retry</Button>}
        >
          {error || "Unable to load QR codes or scan totals."}
        </Alert>
      )}
      {showTrends && (
        <Card sx={{ mb: 2 }}>
          <Typography level="title-lg">Tracked scans · last 30 days</Typography>
          <Typography level="body-sm">
            {stats.data?.tracking_note ||
              "Calls, SMS, email, and raw vCards are not tracked."}
          </Typography>
          <Box
            role="img"
            aria-label={days
              .map((day) => `${day.date}: ${day.scans} scans`)
              .join("; ")}
            sx={{ display: "flex", gap: "3px", height: 120, alignItems: "end" }}
          >
            {days.map((day) => (
              <Box
                key={day.date}
                title={`${day.date}: ${day.scans} scans`}
                sx={{
                  flex: 1,
                  minHeight: 2,
                  height: `${(day.scans / maxScans) * 100}%`,
                  bgcolor: day.scans ? "primary.500" : "neutral.200",
                  borderRadius: "3px 3px 0 0",
                }}
              />
            ))}
          </Box>
          <Stack direction="row" justifyContent="space-between">
            <Typography level="body-xs">{days[0].date}</Typography>
            <Typography level="body-xs">{days[29].date}</Typography>
          </Stack>
        </Card>
      )}
      <CollapsibleFilters><Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
        <Input
          placeholder="Search name, destination, or campaign"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ flex: 1 }}
        />
        <Select
          value={kind}
          onChange={(_, value) => setKind(value || "all")}
          aria-label="Destination type"
        >
          <Option value="all">All types</Option>
          {[
            "website",
            "review_link",
            "call",
            "sms",
            "email",
            "payment",
            "whatsapp",
            "funnel",
            "form",
            "survey",
            "quiz",
            "profile_card",
            "vcard",
            "business_card",
          ].map((type) => (
            <Option key={type} value={type}>
              {type.replaceAll("_", " ")}
            </Option>
          ))}
        </Select>
        <Select
          value={status}
          onChange={(_, value) => setStatus(value || "all")}
          aria-label="Code status"
        >
          <Option value="all">All statuses</Option>
          {["active", "paused", "archived"].map((value) => (
            <Option key={value} value={value}>
              {value}
            </Option>
          ))}
        </Select>
      </Stack></CollapsibleFilters>
      {selected.size > 0 && (
        <Alert
          sx={{ mb: 2 }}
          endDecorator={
            <Button
              color="danger"
              disabled={busy}
              onClick={() => setDeleteIds([...selected])}
            >
              Delete selected
            </Button>
          }
        >
          {selected.size} selected across pages{" "}
          <Button variant="plain" onClick={() => setSelected(new Set())}>
            Clear selection
          </Button>
        </Alert>
      )}
      {query.isLoading ? (
        <Typography>Loading QR codes…</Typography>
      ) : !query.isError && !query.data?.results.length ? (
        <Card>
          <Typography>No codes match these filters.</Typography>
          <Typography level="body-sm">
            Create a QR code or clear the filters to see more campaigns.
          </Typography>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {query.data?.results.map((record) => (
            <Grid key={record.id} xs={12} sm={6} md={4} lg={3}>
              <Card sx={{ height: "100%" }}>
                <Stack direction="row" justifyContent="space-between">
                  <Checkbox
                    aria-label={`Select ${record.name}`}
                    checked={selected.has(record.id)}
                    onChange={() =>
                      setSelected((previous) => {
                        const next = new Set(previous);
                        if (next.has(record.id)) next.delete(record.id);
                        else next.add(record.id);
                        return next;
                      })
                    }
                  />
                  <Chip
                    color={record.status === "active" ? "success" : "neutral"}
                  >
                    {record.status}
                  </Chip>
                </Stack>
                <QRThumbnail record={record} />
                <Typography level="title-lg">{record.name}</Typography>
                <Typography level="body-sm" sx={{ overflowWrap: "anywhere" }}>
                  {record.destination_url || "No destination configured"}
                </Typography>
                <Typography level="body-xs">
                  {record.campaign || "No campaign label"} ·{" "}
                  {record.is_trackable
                    ? `${record.scan_count} tracked scans`
                    : "Direct code · scans not tracked"}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                  <Button
                    size="sm"
                    onClick={() => {
                      setEditing(record);
                      setOpen(true);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outlined"
                    disabled={busy}
                    onClick={() => download(record, "png")}
                  >
                    PNG
                  </Button>
                  <Button
                    size="sm"
                    variant="outlined"
                    disabled={busy}
                    onClick={() => download(record, "pdf")}
                  >
                    PDF
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                  {record.is_trackable && (
                    <Button
                      size="sm"
                      variant="plain"
                      disabled={busy}
                      onClick={() =>
                        operate(() =>
                          api.patch(`/api/marketing/qr-codes/${record.id}/`, {
                            status:
                              record.status === "active" ? "paused" : "active",
                          }),
                        )
                      }
                    >
                      {record.status === "active"
                        ? "Pause link"
                        : "Activate link"}
                    </Button>
                  )}
                  {record.is_trackable && record.status !== "archived" && (
                    <Button
                      size="sm"
                      variant="plain"
                      disabled={busy}
                      onClick={() =>
                        operate(() =>
                          api.patch(`/api/marketing/qr-codes/${record.id}/`, {
                            status: "archived",
                          }),
                        )
                      }
                    >
                      Archive link
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="plain"
                    color="danger"
                    disabled={busy}
                    onClick={() => setDeleteIds([record.id])}
                  >
                    Delete
                  </Button>
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      <Stack direction="row" spacing={2} sx={{ mt: 2 }} alignItems="center">
        <Button
          disabled={page === 1 || query.isLoading}
          onClick={() => setPage(page - 1)}
        >
          Previous
        </Button>
        <Typography>
          {query.data?.count || 0} matching codes · Page {page}
        </Typography>
        <Button
          disabled={!query.data?.next || query.isLoading}
          onClick={() => setPage(page + 1)}
        >
          Next
        </Button>
      </Stack>
      <Modal
        open={deleteIds.length > 0}
        onClose={() => {
          if (!busy) setDeleteIds([]);
        }}
      >
        <ModalDialog>
          <ModalClose disabled={busy} />
          <Typography level="h4">
            Delete {deleteIds.length} QR code(s)?
          </Typography>
          <Typography>
            Deletion permanently breaks their printed tracking links. Pause or
            archive tracked codes to retain their URLs.
          </Typography>
          {error && <Alert color="danger">{error}</Alert>}
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              disabled={busy}
              onClick={() => setDeleteIds([])}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              loading={busy}
              onClick={() =>
                operate(async () => {
                  await api.post("/api/marketing/qr-codes/bulk-delete/", {
                    ids: deleteIds,
                  });
                  setSelected(
                    (previous) =>
                      new Set(
                        [...previous].filter((id) => !deleteIds.includes(id)),
                      ),
                  );
                  setDeleteIds([]);
                  toast.success("QR codes deleted");
                })
              }
            >
              Delete permanently
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
      <CreateQRCodeModal
        open={open}
        onClose={() => setOpen(false)}
        editingQRCode={editing}
      />
    </Box>
  );
};
export default QRCodes;
